/**
 * Contract Date Parsing Utilities
 * Validates and parses dates from contract date calculation responses
 */

import type { ContractDateCalculationResponse } from '@/types/contract-dates';

/**
 * Typed error raised when contract date parsing fails.
 * Callers should translate the `code` at the UI boundary rather than using
 * the raw (English) `message`, which is intended for diagnostics only.
 */
export type DateParseErrorCode =
  | 'invalid_start_date'
  | 'missing_start_date'
  | 'invalid_end_date';

export class DateParseError extends Error {
  readonly code: DateParseErrorCode;

  constructor(code: DateParseErrorCode) {
    super(code);
    this.code = code;
    this.name = 'DateParseError';
  }
}

/**
 * Validates if a string is a valid ISO date format (YYYY-MM-DD)
 *
 * @param childBirthdate - The birthdate string to validate
 * @returns true if valid ISO date, false otherwise
 */
export function isValidIsoDate(childBirthdate: string | undefined): boolean {
  if (!childBirthdate) return false;

  // Check if it's a non-empty string and matches ISO date format (YYYY-MM-DD)
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(childBirthdate)) return false;

  // Check if it's a valid date
  const date = new Date(childBirthdate);
  return !isNaN(date.getTime());
}

/**
 * Parses and validates dates from contract calculation response
 *
 * @param response - The contract date calculation response
 * @returns Object containing parsed startDate, endDate, and optional kindergartenDate
 * @throws Error if dates are invalid or missing
 */
export function parseContractDates(response: ContractDateCalculationResponse): {
  startDate: Date;
  endDate: Date | null;
  kindergartenDate?: Date;
} {
  let startDate: Date | null = null;
  let endDate: Date | null = null;
  let kindergartenDate: Date | undefined = undefined;

  // Parse and validate start date (required)
  if (response.startDate) {
    startDate = new Date(response.startDate);
    if (isNaN(startDate.getTime())) {
      throw new DateParseError('invalid_start_date');
    }
  } else {
    throw new DateParseError('missing_start_date');
  }

  // Parse and validate end date (optional)
  if (response.endDate) {
    endDate = new Date(response.endDate);
    if (isNaN(endDate.getTime())) {
      throw new DateParseError('invalid_end_date');
    }
  }

  // Parse kindergarten date if available (optional supplementary info)
  if (response.metadata?.kindergartenStartDate) {
    try {
      kindergartenDate = new Date(response.metadata.kindergartenStartDate);
      if (isNaN(kindergartenDate.getTime())) {
        // Invalid kindergarten date is not critical - just ignore it
        console.warn('Invalid kindergarten start date:', response.metadata.kindergartenStartDate);
        kindergartenDate = undefined;
      }
    } catch (_error) {
      // Silently ignore invalid kindergarten date - it's supplementary info
      console.warn('Failed to parse kindergarten start date:', response.metadata.kindergartenStartDate);
      kindergartenDate = undefined;
    }
  }

  return {
    startDate,
    endDate,
    kindergartenDate,
  };
}